import { A } from '@mobily/ts-belt';
import { z } from 'zod';

import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export type NationalIdentifierValidationError =
  | 'too short'
  | 'too long'
  | 'wrong format'
  | 'unsupported chars'
  | 'fraudulent entries';

export const validateNationalIdentifier = (
  countryCode: string,
  string: string,
): NationalIdentifierValidationError | undefined => {
  // All non-empty values are valid if the validator is not present for specified country
  const validator = nationalIdentifiersValidators[countryCode] ?? nonEmpty;
  const result = validator.safeParse(string);
  if (result.success) {
    const isValid = validateUserIdentifier(string);
    return isValid ? undefined : 'fraudulent entries';
  } else if (result.error.message.includes('too_small')) {
    return 'too short';
  } else if (result.error.message.includes('too_big')) {
    return 'too long';
  } else if (result.error.message.includes('regex')) {
    return 'unsupported chars';
  } else {
    return 'wrong format';
  }
};

const nonEmpty = ZodTrimmedString.min(4);

const minMaxAlphanumerics = (min: number, max: number) => {
  return ZodTrimmedString.regex(/^[\da-zA-Z]*$/)
    .max(max)
    .min(min);
};

const minMaxDigits = (min: number, max: number) => {
  return ZodTrimmedString.min(min).max(max).regex(/^\d+$/);
};

const fourToFourteenAlphanumerics = minMaxAlphanumerics(4, 14);

const exactlyAlphanumericCharacters = (number: number) => {
  return minMaxAlphanumerics(number, number);
};

const exactlyCharactersDigitsOnly = (number: number) => {
  return minMaxDigits(number, number);
};

const exactlyAlphaNumericsAndDash = (number: number) => {
  return ZodTrimmedString.min(number)
    .max(number)
    .regex(/^[a-zA-Z0-9-]*$/);
};

// If this is modified we need to update gen national identifier
// LINK projects/backend/tests/utils/faker.ts#gen-national-identifier
const nationalIdentifiersValidators: { [key: string]: z.ZodString } = {
  AR: exactlyAlphanumericCharacters(9),
  BE: exactlyCharactersDigitsOnly(11),
  BG: exactlyCharactersDigitsOnly(10),
  CA: exactlyAlphanumericCharacters(8),
  CN: exactlyAlphanumericCharacters(9),
  HR: exactlyCharactersDigitsOnly(11),
  CY: minMaxAlphanumerics(7, 9),
  CZ: minMaxDigits(9, 10),
  DK: exactlyCharactersDigitsOnly(10),
  EE: exactlyCharactersDigitsOnly(11),
  FI: exactlyAlphaNumericsAndDash(11),
  GI: exactlyCharactersDigitsOnly(9),
  GR: exactlyCharactersDigitsOnly(10),
  IS: exactlyCharactersDigitsOnly(10),
  IN: exactlyAlphanumericCharacters(8),
  IM: exactlyCharactersDigitsOnly(9),
  IT: exactlyAlphanumericCharacters(16),
  JE: exactlyCharactersDigitsOnly(9),
  JP: exactlyAlphanumericCharacters(9),
  JS: exactlyCharactersDigitsOnly(9),
  LV: exactlyAlphaNumericsAndDash(12),
  LI: exactlyAlphanumericCharacters(6),
  LT: exactlyCharactersDigitsOnly(11),
  MT: exactlyAlphanumericCharacters(8),
  NL: exactlyAlphanumericCharacters(9),
  NO: exactlyCharactersDigitsOnly(11),
  PL: exactlyCharactersDigitsOnly(11),
  PT: exactlyCharactersDigitsOnly(9),
  RO: exactlyCharactersDigitsOnly(13),
  RU: minMaxDigits(9, 10),
  SK: exactlyCharactersDigitsOnly(10),
  SI: exactlyCharactersDigitsOnly(13),
  ES: minMaxAlphanumerics(9, 9),
  SE: minMaxDigits(10, 12),
  CH: exactlyAlphanumericCharacters(8),
  TR: minMaxAlphanumerics(7, 9),
  UA: exactlyAlphanumericCharacters(8),
  GB: exactlyAlphanumericCharacters(9),
};

const fourToFourteenCountries = [
  'AF',
  'AX',
  'AL',
  'DZ',
  'AS',
  'AD',
  'AO',
  'AI',
  'AQ',
  'AG',
  'AM',
  'AW',
  'AU',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BZ',
  'BJ',
  'BM',
  'BT',
  'BO',
  'BQ',
  'BA',
  'BW',
  'BV',
  'BR',
  'IO',
  'BN',
  'BF',
  'BI',
  'CV',
  'KH',
  'CM',
  'KY',
  'CF',
  'TD',
  'CL',
  'CX',
  'CC',
  'CO',
  'KM',
  'CD',
  'CG',
  'CK',
  'CR',
  'CI',
  'CU',
  'CW',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'SZ',
  'ET',
  'FK',
  'FO',
  'FJ',
  'GF',
  'PF',
  'TF',
  'GA',
  'GM',
  'GE',
  'GH',
  'GL',
  'GD',
  'GP',
  'GU',
  'GT',
  'GG',
  'GN',
  'GW',
  'GY',
  'HT',
  'HM',
  'VA',
  'HN',
  'HK',
  'ID',
  'IR',
  'IQ',
  'IL',
  'JM',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KP',
  'KR',
  'KW',
  'KG',
  'LA',
  'LB',
  'LS',
  'LR',
  'LY',
  'MO',
  'MK',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MH',
  'MQ',
  'MR',
  'MU',
  'YT',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MS',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NC',
  'NZ',
  'NI',
  'NE',
  'NG',
  'NU',
  'NF',
  'MP',
  'OM',
  'PK',
  'PW',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PN',
  'PR',
  'QA',
  'RE',
  'RW',
  'BL',
  'SH',
  'KN',
  'LC',
  'MF',
  'PM',
  'VC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SX',
  'SB',
  'SO',
  'ZA',
  'GS',
  'SS',
  'LK',
  'SD',
  'SR',
  'SJ',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TK',
  'TO',
  'TT',
  'TN',
  'TM',
  'TC',
  'TV',
  'UG',
  'AE',
  'UM',
  'UY',
  'UZ',
  'VU',
  'VE',
  'VN',
  'VG',
  'VI',
  'WF',
  'EH',
  'YE',
  'ZM',
  'ZW',
];

fourToFourteenCountries.forEach((code) => {
  nationalIdentifiersValidators[code] = fourToFourteenAlphanumerics;
});

export { nationalIdentifiersValidators };

const MIN_FRAUDULENT_SEQUENCE_LENGTH = 6;

export const validateUserIdentifier = (string: string): boolean =>
  string.split('').reduce<boolean>((result, _symbol, index, stringArray) => {
    if (!result) {
      return result; // fraudulent sequence is already found
    }
    const symbols = stringArray.slice(index, index + MIN_FRAUDULENT_SEQUENCE_LENGTH);
    if (symbols.length < MIN_FRAUDULENT_SEQUENCE_LENGTH) {
      return result;
    }
    if (symbols.every((symbol) => symbol === symbols[0])) {
      return false; // catches sequence of same symbols
    }

    if (
      compareSymbolCodes(
        symbols,
        (currentSymbolCharCode, nextSymbolCharCode) =>
          nextSymbolCharCode - currentSymbolCharCode === 1,
      )
    ) {
      return false; // catches sequence of consecutive symbols
    }

    if (
      compareSymbolCodes(
        symbols,
        (currentSymbolCharCode, nextSymbolCharCode) =>
          currentSymbolCharCode - nextSymbolCharCode === 1,
      )
    ) {
      return false; // catches sequence of consecutive symbols in reverse order
    }

    return result;
  }, true);

const compareSymbolCodes = (
  symbols: string[],
  comparator: (arg0: number, arg1: number) => boolean,
): boolean =>
  symbols.slice(0, 4).every((symbol, index) => {
    const nextSymbol = A.getUnsafe(symbols, index + 1);
    const nextSymbolCharCode = nextSymbol.charCodeAt(0);
    const currentSymbolCharCode = symbol.charCodeAt(0);
    return comparator(currentSymbolCharCode, nextSymbolCharCode);
  });
